import React, { useState, useContext, useMemo, useEffect } from "react";
import { Button as BootstrapButton, ButtonGroup as BootstrapButtonGroup } from "reactstrap";
import {
  SubscriptionContext,
  mobileSubscriptionLookup,
  webSubscriptionLookup,
  ACTIVE_MOBILE_PRODUCTS,
  ACTIVE_PRICING_DURATIONS,
  getProductTier,
  getTierSportCount,
  getProductDuration,
  isTierVisibleForUser,
} from "src/services/SubscriptionService";
import AnalyticsService from "src/services/AnalyticsService";
import { DEFAULT_ONE_SPORT, DEFAULT_TWO_SPORTS, DEFAULT_THREE_SPORTS } from "src/constants";
import { DurationItem, TierItem } from "src/typings/pricing";
import { ProductOptions } from "../";
import styles from "./styles.scss";
import SportSelector from "src/components/SportSelector";
import { useFreeTrialClaimed } from "src/services/UserService";
import { responsiveImage } from "src/lib/responsive-image";

const getProductBuyText = (subKey: string) => {
  const sportCount = getTierSportCount(getProductTier(subKey));
  const sportCountTxt = ["All Sports", "1 Sport", "2 Sports", "3 Sports"][sportCount]; // prettier-ignore

  const periodly = {
    day: "Daily",
    month: "Monthly",
    year: "Yearly",
    quarter: "Quarterly",
    week: "Weekly",
  }[getProductDuration(subKey)];

  return `${sportCountTxt} ${periodly}`;
};

const sharpIcon = responsiveImage("/icon/sharp-icon.png", [20, 40, 60]);
const SharpIcon = (
  <img
    src={sharpIcon.src}
    srcSet={sharpIcon.srcSet}
    alt="Sharp Tier Icon"
    className="rotoql-pricing-view__tier-sharp-icon"
  />
);

const DEFAULT_DURATION = ACTIVE_PRICING_DURATIONS[0]; // month
const DEFAULT_TIER_INDEX: number = 0;

export const TIER_INDEX_MAX_SPORTS: { [key: number]: number | null } = {
  0: 1,
  1: 2,
  2: 3,
  3: null,
};

function getDefaultSportList(tierIndex: number): string[] {
  switch (tierIndex) {
    case 0:
      return DEFAULT_ONE_SPORT;
    case 1:
      return DEFAULT_TWO_SPORTS;
    case 2:
      return DEFAULT_THREE_SPORTS;
    case 3:
      return [];
    default:
      return DEFAULT_TWO_SPORTS;
  }
}

function getTierTitle(tierIndex: number): string {
  switch (tierIndex) {
    case 0:
      return "Premium Tab";
    case 1:
      return "Pro Tab";
    case 2:
      return "VIP Tab";
    case 3:
      return "Sharp Tab";
    default:
      return "";
  }
}

const getButtonSubtext = (
  tierValue: number,
  currentTierValue: number | undefined,
  upsellText: string | null,
  cancelled: boolean
) => {
  let subtext = upsellText;
  // Check if the user is subscribed
  if (currentTierValue) {
    if (cancelled) {
      subtext = "REACTIVATE";
    }
    if (tierValue > currentTierValue) {
      subtext = "UPGRADE";
    }
    if (tierValue < currentTierValue) {
      subtext = "DOWNGRADE";
    }
    if (tierValue === currentTierValue) {
      subtext = "Current Plan";
    }
  }
  const markup = <span className="rotoql-pricing-view__purchase-button-subtext">{subtext}</span>;
  return subtext ? markup : null;
};

type TierOption = { tier: TierItem; originalIndex: number };

function TierSelector(props: { tierIndex: number; tierOptions: TierOption[]; onSelectTier(tierIndex: number): void }) {
  const { tierIndex, tierOptions, onSelectTier } = props;
  const buttons = tierOptions.map(({ tier, originalIndex }) => {
    const { maxSports, price, durationDisplayAbv } = tier;
    const sportWord = maxSports === "all" || maxSports > 1 ? "Sports" : "Sport";

    return (
      <BootstrapButton
        key={tier.key}
        active={tierIndex === originalIndex}
        onClick={() => onSelectTier(originalIndex)}
        className={`rotoql-pricing-view__sport-button-group-button ${
          tierIndex === originalIndex ? "rotoql-pricing-view__sport-button-group-button--active" : ""
        }`}
      >
        <span className="tier-sports">
          {maxSports} {sportWord}
        </span>
        <span className="tier-price">
          ${price}/{durationDisplayAbv}
        </span>
      </BootstrapButton>
    );
  });
  return <BootstrapButtonGroup className="rotoql-pricing-view__sport-button-group">{buttons}</BootstrapButtonGroup>;
}

function ProductSelector(props: {
  tierIndex: number;
  sportList: string[];
  durationKey: string;
  userSubscription?: any;
  currentTierValue?: number;
  onSelectProduct(tier: TierItem, value: number, index: number): void;
}) {
  const trialClaimed = useFreeTrialClaimed();
  const ctx = useContext(SubscriptionContext);
  const { tierIndex, sportList, durationKey } = props;
  const isDisplayAnnual = durationKey === "year";
  const maxSports = TIER_INDEX_MAX_SPORTS[tierIndex] ?? "All";
  const isSportListFull = maxSports !== "All" ? sportList.length >= maxSports : true;

  const isCurrentSubscription = (tier: TierItem) => {
    const webSub = webSubscriptionLookup[ctx.subscriptionLevel ?? ""];
    const mobileSub = mobileSubscriptionLookup[ctx.subscriptionLevel ?? ""];
    return [webSub, mobileSub].includes(tier.key);
  };

  const currentSubscriptionTier = getProductTier(
    webSubscriptionLookup[ctx.subscriptionLevel ?? ""] ??
      mobileSubscriptionLookup[ctx.subscriptionLevel ?? ""] ??
      ctx.subscriptionLevel
  );

  return (
    <div className="rotoql-pricing-view__purchase-button-container">
      {ACTIVE_MOBILE_PRODUCTS[tierIndex].map((tier: TierItem, index: number) => {
        const isSkipped = isDisplayAnnual && tier.key.includes("month");
        const tierBase = getProductTier(tier.key);
        const isLegacyMiddleTier = tierBase === "pro" || tierBase === "vip";
        const isSameLegacyTierCrossDuration =
          isLegacyMiddleTier && currentSubscriptionTier === tierBase && !isCurrentSubscription(tier);

        if (!tier.active || isSkipped || isSameLegacyTierCrossDuration) {
          return null;
        }

        const tierValue = Math.pow(10, tierIndex) + index;
        const isSharp = tier.key.includes("sharp");
        const isCurrentPlan = isCurrentSubscription(tier);
        const isCurrentPlanCancelled = isCurrentPlan && props.userSubscription.cancelAt;
        const buttonClass = "rotoql-pricing-view__purchase-button";
        const currentFlag = isCurrentPlan && !isSharp ? "--current" : "";
        const sharpFlag = isSharp ? "--sharp" : "";
        const buttonClassName = `${buttonClass}${currentFlag}${sharpFlag}`;
        const isButtonDisabled = !isSportListFull || (isCurrentPlan && !isCurrentPlanCancelled);

        const buttonSubtext = getButtonSubtext(
          tierValue,
          props.currentTierValue,
          tier.upsellText,
          isCurrentPlanCancelled
        );

        return (
          <button
            key={index}
            disabled={isButtonDisabled}
            className={buttonClassName}
            onClick={() => props.onSelectProduct(tier, tierValue, index)}
          >
            <div className="rotoql-pricing-view__purchase-button-text-container">
              {isSharp ? SharpIcon : null}
              {trialClaimed ? "Buy " : "Start Free Trial for "}
              {getProductBuyText(tier.key)}
            </div>
            {!isDisplayAnnual && buttonSubtext}
          </button>
        );
      })}
    </div>
  );
}

export interface PricingViewProps {
  style?: any;
  className?: string;
  subscriptionLevel: string | null;
  tierIndex?: number;
  sportList?: string[];
  source: string | null;
  currentTierValue?: number;
  currentTier?: TierItem;
  duration?: DurationItem;
  userSubscription?: any;
  onSelectProduct(options: ProductOptions): void;
}

export default function PricingView(props: PricingViewProps) {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { subscriptionLevel, duration = DEFAULT_DURATION } = props;
  const trialClaimed = useFreeTrialClaimed();
  const [tierIndex, setTierIndex] = useState<number>(props.tierIndex ?? DEFAULT_TIER_INDEX);
  const [sportList, setSportList] = useState<string[]>(props.sportList ?? getDefaultSportList(tierIndex));
  const [tierTitle, setTierTitle] = useState<string>(getTierTitle(tierIndex));

  const userSubscriptionKeys = useMemo(() => {
    const keys = new Set<string>();
    const currentSubscriptionLevel = props.subscriptionLevel;
    const rawSubscriptionLevel = props.userSubscription?.subscriptionLevel?.name;

    if (currentSubscriptionLevel) {
      keys.add(currentSubscriptionLevel);
    }

    if (rawSubscriptionLevel) {
      keys.add(rawSubscriptionLevel);
    }

    return keys;
  }, [props.subscriptionLevel, props.userSubscription?.subscriptionLevel?.name]);

  const visibleTierOptions = useMemo(() => {
    return duration.tiers
      .map((tier, originalIndex) => ({ tier, originalIndex }))
      .filter(({ tier }) => isTierVisibleForUser(tier.key, userSubscriptionKeys));
  }, [duration.tiers, userSubscriptionKeys]);

  useEffect(() => {
    if (!visibleTierOptions.length) {
      return;
    }

    const isCurrentTierVisible = visibleTierOptions.some((option) => option.originalIndex === tierIndex);
    if (isCurrentTierVisible) {
      return;
    }

    const preferredTierIndex = props.currentTierValue ? 3 : DEFAULT_TIER_INDEX;
    const fallbackOption =
      visibleTierOptions.find((option) => option.originalIndex === preferredTierIndex) ?? visibleTierOptions[0];

    setTierIndex(fallbackOption.originalIndex);
    setTierTitle(getTierTitle(fallbackOption.originalIndex));
    setSportList(getDefaultSportList(fallbackOption.originalIndex));
  }, [tierIndex, visibleTierOptions, props.currentTierValue]);

  const selectTier = (newTierIndex: number) => {
    setTierIndex(newTierIndex);
    setTierTitle(getTierTitle(newTierIndex));
    setSportList(getDefaultSportList(newTierIndex));

    if (newTierIndex !== tierIndex) {
      AnalyticsService.track("Select Tier", {
        tier: tierTitle,
      });
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const selectProduct = (selectedTier: TierItem, tierValue: number, _productIndex: number) => {
    const { currentTierValue, source, onSelectProduct } = props;
    const hasAllSports = selectedTier.maxSports === "all";
    const isSportListFull = selectedTier.maxSports === "all" ? true : sportList.length >= selectedTier.maxSports;

    if (!hasAllSports && !isSportListFull) {
      return;
    }

    let newState: any = {
      sportList,
      selectedTier,
      showDowngrade: false,
      showUpgrade: false,
    };
    let sourceLabel = "";

    if (currentTierValue) {
      if (currentTierValue > tierValue) {
        newState.showDowngrade = true;
        sourceLabel = "Downgrade";
      }
      if (currentTierValue < tierValue) {
        newState.showUpgrade = true;
        sourceLabel = "Upgrade";
      }
    }
    AnalyticsService.track("Checkout Button Click", {
      screen: "PricingView",
      page: "Pricing",
      tab: selectedTier.durationTitle,
      subscriptionLevel: `${selectedTier.title} ${selectedTier.durationTitle}`,
      source: sourceLabel,
      upgradeSource: source,
    });
    onSelectProduct(newState);
  };

  // prettier-ignore
  return (
    <div
      className={`rotoql-pricing-view ${props.className}`}
      style={props.style}
    >
      <div className="rotoql-pricing-view__step-text">STEP 1</div>
      <div className="rotoql-pricing-view__step-subtext">
        How many sports do you want to access?
        {trialClaimed ? "" : <><br/>(Free for 3 days)</>}
      </div>
      <TierSelector
        tierIndex={tierIndex}
        tierOptions={visibleTierOptions}
        onSelectTier={selectTier}
      />
      <div className="rotoql-pricing-view__step-text">STEP 2</div>
      <div className="rotoql-pricing-view__step-subtext">
        Select your sport(s):
        <div className="rotoql-pricing-view__change-sport-reminder">
          * You can change your sport 1x per week
        </div>
      </div>
      <SportSelector
        tier={getProductTier(ACTIVE_MOBILE_PRODUCTS[tierIndex][0].key)}
        sportList={sportList}
        setSportList={setSportList}
        className="rotoql-pricing-view__sport-selector-container"
      />
      <ProductSelector
        tierIndex={tierIndex}
        sportList={sportList}
        durationKey={duration.key}
        userSubscription={props.userSubscription}
        currentTierValue={props.currentTierValue}
        onSelectProduct={selectProduct}
      />
      <style jsx>{styles}</style>
    </div>
  );
}
